import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { signOut } from "@/contexts/AuthContext";
import { useRefreshToken } from "@/hooks/useRefreshToken";
import { v1 as uuidv1 } from "uuid";
import jwtDecode from "jwt-decode";
import { validateAuthOrSignOut } from "./authGuard";

interface DecodedToken {
  exp: number;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;

function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp - currentTime < 120;
  } catch {
    return true;
  }
}

function getAPIClient() {
  const token = validateAuthOrSignOut();
  const { sendRefreshToken } = useRefreshToken();

  const api = axios.create({
    baseURL: `${import.meta.env.VITE_API_URL}`,
    headers: {
      Authorization: `Bearer ${token}`,
      "access-control-allow-origin": "*",
    },
  });

  api.interceptors.request.use(
    async (config) => {
      const currentToken = localStorage.getItem("barte-auth-token");
      const refreshTokenStorage = localStorage.getItem("barte-refresh-token");
      if (currentToken && isTokenExpired(currentToken) && !isRefreshing) {
        isRefreshing = true;
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}public/auth/refresh-token`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                refreshToken: String(refreshTokenStorage),
              }),
            },
          );
          const data = await response.json();
          const {
            email,
            roles,
            token: newToken,
            idSeller,
            document,
            refreshToken,
          } = data;
          localStorage.setItem("barte-auth-token", newToken);
          localStorage.setItem(
            "barte-user-info",
            JSON.stringify({ email, roles, idSeller, document }),
          );
          localStorage.setItem("barte-refresh-token", refreshToken);
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (error) {
          signOut();
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      const uuid = uuidv1();
      config.headers.post["x-idempotency-key"] = uuid;
      return config;
    },
    (error) => Promise.reject(error),
  );

  api.interceptors.response.use(
    (response) => {
      return Promise.resolve(response);
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as CustomAxiosRequestConfig;

      if (
        error?.response?.status === 401 &&
        !originalRequest?._retry &&
        !isRefreshing
      ) {
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await sendRefreshToken();
          const newToken = localStorage.getItem("barte-auth-token");
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (error) {
          signOut();
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      if (error?.response?.status === 403) {
        signOut();
      }
      return Promise.reject(error);
    },
  );

  return api;
}

export const Api = getAPIClient();
